import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Users, Key, GraduationCap, Mail } from 'lucide-react';
import { getUser } from '../lib/auth';
import MemberManagement from './MemberManagement';
import UserManagement from './UserManagement';
import APICredentialsManagement from './APICredentialsManagement';
import SensorManagement from './SensorManagement';
import MemberOrgUserManagement from './MemberOrgUserManagement';
import MemberEmailDomainManagement from './MemberEmailDomainManagement';

type Tab = 'members' | 'users' | 'sensors' | 'api' | 'org-users' | 'email-domains';

export default function MembersAndUsers() {
  const currentUser = getUser();
  const canManageUsers = currentUser?.role === 'admin' || currentUser?.role === 'systemadmin';
  const canManageAPI = currentUser?.role === 'admin' || currentUser?.role === 'systemadmin';
  const isMemberAdmin = currentUser?.role === 'member' && (currentUser as any)?.is_member_admin === true;

  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab') as Tab | null;
  const memberIdParam = searchParams.get('member_id');

  const validTabs: Tab[] = ['members', 'users', 'sensors', 'api', 'org-users', 'email-domains'];
  const [activeTab, setActiveTab] = useState<Tab>(
    tabParam && validTabs.includes(tabParam) ? tabParam : 'members'
  );
  const [filterMemberId, setFilterMemberId] = useState<string | null>(memberIdParam);

  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
    setFilterMemberId(memberIdParam);
  }, [tabParam, memberIdParam]);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    if (tab !== 'users') {
      setFilterMemberId(null);
      setSearchParams({ tab });
    } else {
      if (filterMemberId) {
        setSearchParams({ tab, member_id: filterMemberId });
      } else {
        setSearchParams({ tab });
      }
    }
  };

  const clearMemberFilter = () => {
    setFilterMemberId(null);
    setSearchParams({ tab: 'users' });
  };

  // Tab-bar synlighet
  const visibleTabCount =
    1 + // members alltid
    (canManageUsers ? 2 : 0) + // users + sensors
    (canManageAPI ? 1 : 0) + // api
    (isMemberAdmin ? 2 : 0); // org-users + email-domains

  const tabBtn = (active: boolean) => `px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
    active ? 'border-kf-primary text-kf-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
  }`;

  return (
    <div className="space-y-6">
      {visibleTabCount > 1 && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="border-b">
            <nav className="flex -mb-px overflow-x-auto">
              <button onClick={() => handleTabChange('members')} className={tabBtn(activeTab === 'members')}>
                <Building2 size={18} />
                {currentUser?.role === 'member' ? 'Min organisasjon' : 'Medlemsorganisasjoner'}
              </button>

              {/* Medlem-admin-tabs (kun for member med is_member_admin) */}
              {isMemberAdmin && (
                <>
                  <button onClick={() => handleTabChange('org-users')} className={tabBtn(activeTab === 'org-users')}>
                    <Users size={18} />
                    Brukere
                  </button>
                  <button onClick={() => handleTabChange('email-domains')} className={tabBtn(activeTab === 'email-domains')}>
                    <Mail size={18} />
                    E-postdomener
                  </button>
                </>
              )}

              {/* Studieforbund/systemadmin-tabs */}
              {canManageUsers && (
                <button onClick={() => handleTabChange('users')} className={tabBtn(activeTab === 'users')}>
                  <Users size={18} />
                  Brukere
                </button>
              )}
              {canManageUsers && (
                <button onClick={() => handleTabChange('sensors')} className={tabBtn(activeTab === 'sensors')}>
                  <GraduationCap size={18} />
                  Sensorer
                </button>
              )}
              {canManageAPI && (
                <button onClick={() => handleTabChange('api')} className={tabBtn(activeTab === 'api')}>
                  <Key size={18} />
                  API
                </button>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'members' && <MemberManagement />}
      {activeTab === 'users' && canManageUsers && (
        <UserManagement filterMemberId={filterMemberId} onClearFilter={clearMemberFilter} />
      )}
      {activeTab === 'sensors' && canManageUsers && <SensorManagement />}
      {activeTab === 'api' && canManageAPI && <APICredentialsManagement />}
      {activeTab === 'org-users' && isMemberAdmin && currentUser?.member_id && (
        <MemberOrgUserManagement memberId={currentUser.member_id} />
      )}
      {activeTab === 'email-domains' && isMemberAdmin && currentUser?.member_id && (
        <MemberEmailDomainManagement memberId={currentUser.member_id} />
      )}
    </div>
  );
}
